import { useId } from 'react';
import { Box, Typography } from '@mui/material';
import { useReducedMotion } from 'framer-motion';

import Panel from './ui/Panel';
import { FONT, RADIUS } from '../theme/tokens';

// ---------------------------------------------------------------------------
// Curba S — progres cumulat, planificat față de realizat.
//
// Geometria e legată de scala grilei: y = 168 înseamnă 0%, y = 16 înseamnă
// 100%, deci un procent p se citește la  y = 168 − p × 1,52.
// Cifrele de sub grafic spun „Planificat 72% / Realizat 68%", adică execuția e
// ÎN URMĂ față de plan, deci curba realizată trebuie să rămână SUB cea
// punctată pe toată lungimea ei:
//
//   planificat, la reperul AZI:  y = 58,6   → (168 − 58,6) / 1,52 ≈ 72%
//   realizat,   la reperul AZI:  y = 64,6   → (168 − 64,6) / 1,52 ≈ 68%
//
// Culorile vin din variabilele temei, deci graficul se citește la fel pe fundal
// deschis și pe fundal închis, fără a doua versiune a desenului.
// ---------------------------------------------------------------------------

const TODAY_X = 250;
const PLANNED_Y = 58.6;
const ACTUAL_Y = 64.6;

const PLANNED_D = 'M40,168 C 100,167 140,146 176,118 S 232,68 250,58.6 S 330,22 388,16';
const ACTUAL_D = 'M40,168 C 100,167.5 142,152 178,126 S 230,80 250,64.6';
const ACTUAL_AREA_D = `${ACTUAL_D} L 250,168 L 40,168 Z`;

export default function SCurve({ t, sx }) {
  const reduce = useReducedMotion();
  const gradientId = `qf-area-${useId().replace(/:/g, '')}`;

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      {/* ── Aura ──
          Un strat care nu desenează nimic în interior: fundal transparent, deci
          din el se vede doar umbra, iar o umbră exterioară se pictează strict în
          afara casetei. Așa halo-ul îmbrățișează chenarul fără să tulbure sticla
          de dedesubt.

          Stă aprinsă constant, nu pulsează: fiind o suprafață pictată o singură
          dată, nu costă niciun cadru cât timp pagina e deschisă.

          Culoarea e chiar cea a curbei realizate, luată din același token. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: `${RADIUS.xl}px`,
          pointerEvents: 'none',
          opacity: 0.32,
          // Un fir subțire chiar pe chenar, plus halo-ul care se stinge în afară.
          boxShadow: `0 0 0 1px var(--qf-amber-mark),
                      0 0 22px 1px var(--qf-amber-mark),
                      0 0 46px 6px var(--qf-amber-mark)`,
        }}
      />

      <Panel
        variant="glass"
        header={{ title: t('home.chartTitle'), meta: t('home.chartRange') }}
        sx={{ position: 'relative' }}
      >
      <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2.5, pb: 1 }}>
        <Box
          component="svg"
          viewBox="0 0 400 200"
          role="img"
          aria-label={t('home.chartAlt')}
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            overflow: 'visible',

            // Keyframe-urile sunt declarate în ACELAȘI obiect `sx` în care sunt
            // folosite; altfel emotion le scopează separat și nu se aplică.
            '@keyframes qfDraw': { from: { strokeDashoffset: 700 }, to: { strokeDashoffset: 0 } },
            '@keyframes qfFade': { from: { opacity: 0 }, to: { opacity: 1 } },
            '@keyframes qfPulse': {
              '0%': { r: 4.5, opacity: 0.5 },
              '70%': { r: 13, opacity: 0 },
              '100%': { r: 13, opacity: 0 },
            },

            '& .grid': { stroke: 'var(--qf-line)', strokeWidth: 1 },
            '& .axis': {
              fill: 'var(--qf-ink-3)',
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: '.06em',
            },

            '& .planned': {
              fill: 'none',
              stroke: 'var(--qf-ink-3)',
              strokeWidth: 1.6,
              strokeDasharray: reduce ? '5 5' : 700,
              animation: reduce ? 'none' : 'qfDraw 1.6s cubic-bezier(.28,.11,.32,1) .3s both',
            },
            '& .actual': {
              fill: 'none',
              stroke: 'var(--qf-amber)',
              strokeWidth: 2.6,
              strokeLinecap: 'round',
              strokeDasharray: reduce ? 'none' : 700,
              animation: reduce ? 'none' : 'qfDraw 1.5s cubic-bezier(.28,.11,.32,1) .7s both',
            },
            '& .area': { animation: reduce ? 'none' : 'qfFade .8s ease 1.5s both' },
            '& .today': {
              stroke: 'var(--qf-line-2)',
              strokeWidth: 1,
              strokeDasharray: '3 4',
              animation: reduce ? 'none' : 'qfFade .5s ease 1.5s both',
            },
            '& .head': { animation: reduce ? 'none' : 'qfFade .4s ease 1.9s both' },
            '& .ring': { animation: reduce ? 'none' : 'qfPulse 2.6s ease-out 2.2s infinite' },
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--qf-amber)" stopOpacity="0.22" />
              <stop offset="100%" stopColor="var(--qf-amber)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Linii de referință: 0 / 25 / 50 / 75 / 100% */}
          {[16, 54, 92, 130, 168].map((y) => (
            <line key={y} className="grid" x1="40" y1={y} x2="388" y2={y} />
          ))}

          {/* Etichetele axelor */}
          <text className="axis" x="0" y="20">100%</text>
          <text className="axis" x="8" y="96">50%</text>
          <text className="axis" x="24" y="172">0</text>
          <text className="axis" x="36" y="190">L1</text>
          <text className="axis" x="190" y="190">L6</text>
          <text className="axis" x="366" y="190">L12</text>

          {/* Suprafața de sub curba realizată — dă masă execuției de până acum */}
          <path className="area" d={ACTUAL_AREA_D} fill={`url(#${gradientId})`} stroke="none" />

          <path className="planned" d={PLANNED_D} />
          <path className="actual" d={ACTUAL_D} />

          {/* Reperul „azi" */}
          <line className="today" x1={TODAY_X} y1="8" x2={TODAY_X} y2="168" />
          <text className="axis head" x={TODAY_X} y="6" textAnchor="middle">
            {t('home.chartToday')}
          </text>

          {/* Capătul curbei realizate, cu inelul care pulsează */}
          <g className="head">
            <circle className="ring" cx={TODAY_X} cy={ACTUAL_Y} r="4.5" fill="none" stroke="var(--qf-amber)" strokeWidth="1.5" />
            <circle cx={TODAY_X} cy={ACTUAL_Y} r="4.5" fill="var(--qf-amber)" />
          </g>

          {/* Punctul de plan la aceeași dată, ca diferența să fie citibilă */}
          <circle
            className="head"
            cx={TODAY_X}
            cy={PLANNED_Y}
            r="3.5"
            fill="var(--qf-card)"
            stroke="var(--qf-ink-2)"
            strokeWidth="1.5"
          />
        </Box>
      </Box>

      {/* Citirile numerice */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid var(--qf-glass-line)' }}>
        {[
          { k: t('home.readPlanned'), v: t('home.readPlannedVal'), c: 'var(--qf-ink)' },
          { k: t('home.readActual'), v: t('home.readActualVal'), c: 'var(--qf-amber)' },
        ].map((row, i) => (
          <Box
            key={row.k}
            sx={{
              px: { xs: 2, sm: 2.75 },
              py: 1.75,
              minWidth: 0,
              borderRight: i === 0 ? '1px solid var(--qf-glass-line)' : 'none',
            }}
          >
            <Typography
              component="div"
              sx={{
                fontFamily: FONT.mono,
                fontSize: 11.5,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: 'var(--qf-ink-3)',
                mb: 0.75,
              }}
            >
              {row.k}
            </Typography>
            <Typography
              component="div"
              sx={{
                fontFamily: FONT.mono,
                // Cifrele au toate aceeași lățime, deci coloanele nu joacă.
                fontVariantNumeric: 'tabular-nums',
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: row.c,
              }}
            >
              {row.v}
            </Typography>
            </Box>
          ))}
        </Box>
      </Panel>
    </Box>
  );
}
